/**
 * What a match log says about one player.
 *
 * Everything here is derived from matches that are already on the client for another
 * reason — the profile lists them — so a record, a form line and a partner table cost no
 * requests. Requirements §5.1 asks for exactly these and notes that partner win rates are
 * the most-viewed screen in comparable apps.
 *
 * Only `confirmed` matches count, everywhere. A pending match has not been agreed to, a
 * disputed one is being argued about, and a void one did not happen; none of them have
 * moved anyone's rating, so none of them belong in a record that sits next to it.
 */
import type { MatchSummary } from "./types.js";

/**
 * How much history a profile is built from.
 *
 * A partner table over a year of play is the interesting one; a partner table over an
 * entire career costs a page load to answer a question nobody asked. The profile says so
 * out loud when someone has played more than this, rather than quietly reporting a partial
 * answer as the whole truth.
 */
export const PROFILE_HISTORY = 100;

export interface WonLost {
  won: number;
  lost: number;
}

export function isCounted(match: MatchSummary): boolean {
  return match.status === "confirmed";
}

function sideOf(match: MatchSummary, memberId: string): "A" | "B" | null {
  return match.participants.find((p) => p.memberId === memberId)?.side ?? null;
}

/** Win-loss over the confirmed matches given. */
export function recordFor(matches: MatchSummary[], memberId: string): WonLost {
  return matches.filter(isCounted).reduce<WonLost>(
    (tally, match) => {
      const side = sideOf(match, memberId);
      if (!side) return tally;
      return match.winner === side
        ? { won: tally.won + 1, lost: tally.lost }
        : { won: tally.won, lost: tally.lost + 1 };
    },
    { won: 0, lost: 0 },
  );
}

export function winRate(record: WonLost): number | null {
  const played = record.won + record.lost;
  return played === 0 ? null : Math.round((record.won / played) * 100);
}

/**
 * The last `count` results, most recent first — the same order the matches arrive in.
 * Newest first is deliberate: form is read left to right as "what happened lately", and a
 * chronological line puts the answer at the far end.
 */
export function formFor(matches: MatchSummary[], memberId: string, count = 10): boolean[] {
  return matches
    .filter(isCounted)
    .filter((match) => sideOf(match, memberId) !== null)
    .slice(0, count)
    .map((match) => match.winner === sideOf(match, memberId));
}

export interface PlayerTally extends WonLost {
  memberId: string;
  displayName: string;
  avatarUrl: string | null;
  played: number;
}

/**
 * Who this member has played with, and how it went. Doubles only by construction — nobody
 * partners anybody in singles, so a singles match contributes no partners rather than
 * being filtered out as a special case.
 */
export function partnersFor(matches: MatchSummary[], memberId: string): PlayerTally[] {
  return tally(matches, memberId, "with");
}

/** Who this member has played against. Head-to-head, from their side of the net. */
export function opponentsFor(matches: MatchSummary[], memberId: string): PlayerTally[] {
  return tally(matches, memberId, "against");
}

function tally(
  matches: MatchSummary[],
  memberId: string,
  relation: "with" | "against",
): PlayerTally[] {
  const byMember = new Map<string, PlayerTally>();

  for (const match of matches) {
    if (!isCounted(match)) continue;
    const side = sideOf(match, memberId);
    if (!side) continue;
    const won = match.winner === side;

    for (const other of match.participants) {
      if (other.memberId === memberId) continue;
      const sameSide = other.side === side;
      if (sameSide !== (relation === "with")) continue;

      const entry = byMember.get(other.memberId) ?? {
        memberId: other.memberId,
        displayName: other.displayName,
        avatarUrl: other.avatarUrl,
        played: 0,
        won: 0,
        lost: 0,
      };

      byMember.set(other.memberId, {
        ...entry,
        played: entry.played + 1,
        won: entry.won + (won ? 1 : 0),
        lost: entry.lost + (won ? 0 : 1),
      });
    }
  }

  // Most played first, and a better record breaks the tie. Sorting by win rate alone would
  // put one lucky match above a hundred honest ones.
  return [...byMember.values()].sort(
    (a, b) => b.played - a.played || b.won - a.won || a.displayName.localeCompare(b.displayName),
  );
}
